import { createContext, useContext, useEffect, useState, ReactNode } from 'react'

// Aparencia claro/escuro DA MESA DE JOGO, escopada a sessao (redesign
// 2026-07-18): o provider monta no GameSession e aplica a classe .dark e o
// atributo data-game-appearance no <body> ENQUANTO a mesa esta aberta, com
// cleanup no unmount. O <html> nunca recebe .dark — fora da mesa o app segue
// o tema da conta (ThemeContext). Aplica no body (e nao so no container)
// porque os overlays Radix (Dialog, DropdownMenu, Sheet) portalam para la.
type GameAppearance = 'light' | 'dark' | 'system'

interface GameAppearanceContextType {
  appearance: GameAppearance
  resolvedAppearance: 'light' | 'dark'
  setAppearance: (appearance: GameAppearance) => void
}

const GameAppearanceContext = createContext<GameAppearanceContextType | undefined>(undefined)

const STORAGE_KEY = 'rpgia-game-appearance'

function resolve(appearance: GameAppearance): 'light' | 'dark' {
  if (appearance === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return appearance
}

export function GameAppearanceProvider({ children }: { children: ReactNode }) {
  const [appearance, setAppearanceState] = useState<GameAppearance>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as GameAppearance | null
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'dark'
  })
  const [resolvedAppearance, setResolvedAppearance] = useState<'light' | 'dark'>(() => resolve(appearance))

  useEffect(() => {
    setResolvedAppearance(resolve(appearance))
    if (appearance === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handler = () => setResolvedAppearance(resolve('system'))
      mediaQuery.addEventListener('change', handler)
      return () => mediaQuery.removeEventListener('change', handler)
    }
  }, [appearance])

  useEffect(() => {
    const body = document.body
    body.classList.toggle('dark', resolvedAppearance === 'dark')
    body.dataset.gameAppearance = resolvedAppearance
    return () => {
      body.classList.remove('dark')
      delete body.dataset.gameAppearance
    }
  }, [resolvedAppearance])

  const setAppearance = (next: GameAppearance) => {
    setAppearanceState(next)
    localStorage.setItem(STORAGE_KEY, next)
  }

  return (
    <GameAppearanceContext.Provider value={{ appearance, resolvedAppearance, setAppearance }}>
      {children}
    </GameAppearanceContext.Provider>
  )
}

export function useGameAppearance() {
  const context = useContext(GameAppearanceContext)
  if (context === undefined) {
    throw new Error('useGameAppearance must be used within a GameAppearanceProvider')
  }
  return context
}
